import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch, apiJson, ApiError } from '@/lib/apiClient';
import { isNotFound } from '@/features/trips/useTrips';
import type {
  TenantScheduleResponse,
  TenantSchedule,
  VehicleWorkingHoursResponse,
  VehicleOverride,
} from '../../../../api/src/modules/afterHours/routes';

/**
 * The working-hours CONFIG surface (slice 37), consumed exactly as the wire
 * contract defines it (seam S1 — types imported from `afterHours/routes.ts`, never
 * transcribed, the overspeed precedent). Two levels:
 *   - the COMPANY schedule (`/api/working-hours`) — absent = OFF (no default);
 *   - the per-VEHICLE override (`/api/vehicles/:id/working-hours`) — company hours,
 *     a custom window, or opt-out (24h vehicle), plus the authorise-next-trip action.
 *
 * Writing is owner/manager on the server; the UI gates the control to match rather
 * than showing a button that will 403. The `effective` resolution comes from the
 * server — the web never re-derives which schedule is in force for a vehicle.
 */

export type {
  TenantScheduleResponse,
  TenantSchedule,
  VehicleWorkingHoursResponse,
  VehicleOverride,
};

export const tenantWorkingHoursKey = ['working-hours', 'tenant'] as const;
export const vehicleWorkingHoursKey = (vehicleId: string) =>
  ['working-hours', 'vehicle', vehicleId] as const;

export function useTenantWorkingHours(): UseQueryResult<TenantScheduleResponse> {
  return useQuery({
    queryKey: tenantWorkingHoursKey,
    queryFn: ({ signal }) => apiJson<TenantScheduleResponse>('/api/working-hours', { signal }),
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}

/** PUT the company schedule — `{ enabled, weekdays, startMin, endMin }`. On success
 *  every vehicle that inherits company hours re-resolves, so the vehicle views are
 *  invalidated too. */
export function useSetTenantWorkingHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TenantSchedule): Promise<TenantScheduleResponse> => {
      const res = await apiFetch('/api/working-hours', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new ApiError(res.status, null);
      return (await res.json()) as TenantScheduleResponse;
    },
    onSuccess: (data) => {
      qc.setQueryData(tenantWorkingHoursKey, data);
      void qc.invalidateQueries({ queryKey: ['working-hours', 'vehicle'] });
    },
  });
}

/** DELETE the company schedule → OFF (unset). */
export function useDeleteTenantWorkingHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ deleted: number }> => {
      const res = await apiFetch('/api/working-hours', { method: 'DELETE' });
      if (!res.ok) throw new ApiError(res.status, null);
      return (await res.json()) as { deleted: number };
    },
    onSuccess: () => {
      qc.setQueryData(tenantWorkingHoursKey, { set: false } satisfies TenantScheduleResponse);
      void qc.invalidateQueries({ queryKey: ['working-hours', 'vehicle'] });
    },
  });
}

export function useVehicleWorkingHours(
  vehicleId: string,
  opts: { enabled?: boolean } = {},
): UseQueryResult<VehicleWorkingHoursResponse> {
  return useQuery({
    queryKey: vehicleWorkingHoursKey(vehicleId),
    enabled: opts.enabled ?? Boolean(vehicleId),
    queryFn: ({ signal }) =>
      apiJson<VehicleWorkingHoursResponse>(
        `/api/vehicles/${encodeURIComponent(vehicleId)}/working-hours`,
        { signal },
      ),
    retry: (count, error) => !isNotFound(error) && count < 2,
  });
}

/** PUT a per-vehicle override — an opt-out or a custom window. */
export function useSetVehicleWorkingHours(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: VehicleOverride): Promise<VehicleWorkingHoursResponse> => {
      const res = await apiFetch(
        `/api/vehicles/${encodeURIComponent(vehicleId)}/working-hours`,
        { method: 'PUT', body: JSON.stringify(body) },
      );
      if (!res.ok) throw new ApiError(res.status, null);
      return (await res.json()) as VehicleWorkingHoursResponse;
    },
    onSuccess: (data) => qc.setQueryData(vehicleWorkingHoursKey(vehicleId), data),
  });
}

/** DELETE the per-vehicle override → the vehicle inherits company hours. */
export function useClearVehicleWorkingHours(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ deleted: number }> => {
      const res = await apiFetch(
        `/api/vehicles/${encodeURIComponent(vehicleId)}/working-hours`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new ApiError(res.status, null);
      return (await res.json()) as { deleted: number };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: vehicleWorkingHoursKey(vehicleId) }),
  });
}

/** POST authorise-next-trip / DELETE revoke, for a vehicle. Both re-read the
 *  vehicle view so the authorised state shown is the server's. */
export function useAuthoriseVehicle(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (authorise: boolean): Promise<{ authorised: boolean }> => {
      const res = await apiFetch(
        `/api/vehicles/${encodeURIComponent(vehicleId)}/working-hours/authorise`,
        { method: authorise ? 'POST' : 'DELETE' },
      );
      if (!res.ok) throw new ApiError(res.status, null);
      return (await res.json()) as { authorised: boolean };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: vehicleWorkingHoursKey(vehicleId) }),
  });
}
